import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  Trash2,
  UploadCloud,
} from 'lucide-react';

import { cn } from 'shared/lib/utils';
import { uploadFile } from 'shared/lib/uploadService';
import { logger } from 'shared/lib/logger';
import { ConfirmModal } from 'shared/components/ui/ConfirmModal';
import AIDocumentScanLoader, {
  type AIProcessingPhase,
} from 'shared/components/AIDocumentScanLoader';
import type { FileMeta } from 'features/onboarding/types/onboarding.types';

const MAX_BYTES = 10 * 1024 * 1024;

const DEFAULT_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

interface FileUploadProps {
  label: string;
  value?: FileMeta;
  onChange: (file: FileMeta | null) => void;
  onUploaded?: (file: FileMeta) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  busy?: boolean;
  busyLabel?: string;
  /** Notifies the parent while the file is being uploaded to storage. */
  onUploadingChange?: (uploading: boolean) => void;
  /** When true, suppress the built-in spinner (replaced by AI scan loader). */
  hideInlineLoader?: boolean;
  /** Inline AI scan phase — renders a compact loader inside the dropzone. */
  processingPhase?: AIProcessingPhase | null;
  onProcessingDone?: () => void;
  /** Override accepted MIME types (passed through to react-dropzone). */
  accept?: Record<string, string[]>;
  /**
   * When provided, the trash icon opens this parent-controlled flow instead of
   * the built-in ConfirmModal. The parent is responsible for calling
   * `onChange(null)` after confirmation. Used by Step7Documents to offer
   * dependent-field reset checkboxes.
   */
  onRequestRemove?: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  value,
  onChange,
  onUploaded,
  required,
  error,
  hint,
  busy,
  busyLabel,
  onUploadingChange,
  hideInlineLoader,
  processingPhase,
  onProcessingDone,
  accept,
  onRequestRemove,
}) => {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState<string | undefined>();
  const [isReading, setIsReading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState<{ url: string; mime: string } | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (localPreview?.url.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview.url);
      }
    };
  }, [localPreview]);

  const onDrop = useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      setLocalError(undefined);

      if (rejected.length > 0) {
        const code = rejected[0].errors[0]?.code;
        if (code === 'file-too-large') {
          setLocalError(t('documents.fileTooLarge') as string);
        } else {
          setLocalError(t('documents.invalidType') as string);
        }
        return;
      }

      const file = accepted[0];
      if (!file) return;

      if (localPreview?.url.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview.url);
      }
      setLocalPreview({ url: URL.createObjectURL(file), mime: file.type });

      setIsReading(true);
      onUploadingChange?.(true);
      try {
        const { url } = await uploadFile(file);
        const meta: FileMeta = {
          name: file.name,
          type: file.type,
          size: file.size,
          url,
          uploadedAt: new Date().toISOString(),
        };
        onUploadingChange?.(false);
        onChange(meta);
        onUploaded?.(meta);
      } catch (err) {
        logger.error(err);
        const message = err instanceof Error ? err.message : (t('documents.invalidType') as string);
        setLocalError(message);
        onUploadingChange?.(false);
      } finally {
        setIsReading(false);
      }
    },
    [onChange, onUploaded, onUploadingChange, localPreview, t],
  );

  const isProcessing = processingPhase != null;
  const previewUrl = value?.url ?? localPreview?.url;
  const previewMime = value?.type ?? localPreview?.mime;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: accept ?? DEFAULT_ACCEPT,
    multiple: false,
    maxSize: MAX_BYTES,
    onDrop,
    noClick: !!value,
    noKeyboard: !!value,
  });

  const errorMessage =
    localError || (error ? t(`validation.${error}`, { defaultValue: error }) : undefined);

  const isImage = value?.type.startsWith('image/');
  const isPdf = value?.type === 'application/pdf';

  return (
    <div className="flex flex-col gap-2 w-full" data-error={errorMessage ? 'true' : undefined}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-gray-500 ms-1">*</span>}
        </span>
        {value && (
          <span className="text-xs text-emerald-600 font-medium">{value.name}</span>
        )}
      </div>

      <div
        {...getRootProps()}
        className={cn(
          'relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed transition-colors p-3 sm:p-4 min-h-[120px] bg-white',
          isDragActive ? 'border-gray-500 bg-gray-50' : 'border-gray-300 hover:border-gray-400',
          errorMessage && 'border-red-400',
          isProcessing && 'border-gray-300 bg-gray-50 cursor-default',
          value && !isProcessing && 'border-solid border-emerald-300 bg-emerald-50/40 cursor-default',
        )}
      >
        <input {...getInputProps()} />

        {isProcessing && processingPhase && (
          <AIDocumentScanLoader
            phase={processingPhase}
            previewUrl={previewUrl}
            previewMime={previewMime}
            onDone={() => {
              if (localPreview?.url.startsWith('blob:')) {
                URL.revokeObjectURL(localPreview.url);
              }
              setLocalPreview(null);
              onProcessingDone?.();
            }}
          />
        )}

        {!isProcessing && !value && !isReading && !busy && (
          <div className="flex flex-col items-center text-center gap-1.5">
            <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
              <UploadCloud size={22} />
            </div>
            <p className="text-sm font-medium text-gray-700">{t('documents.dropOrClick')}</p>
            <p className="text-xs text-gray-500">{t('documents.acceptedFormats')}</p>
          </div>
        )}

        {(isReading || busy) && !hideInlineLoader && (
          <div className="flex flex-col items-center gap-2">
            <div className="loading-spinner h-7 w-7" />
            <span className="text-sm text-gray-600">
              {busyLabel || (t('common.uploading') as string)}
            </span>
          </div>
        )}

        {!isProcessing && value && !isReading && !busy && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 w-full"
          >
            <a
              href={value.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 group"
              title={t('documents.preview') as string}
            >
              {isImage ? (
                <img
                  src={value.url}
                  alt={value.name}
                  className="w-16 h-16 rounded-xl object-cover border border-gray-200 group-hover:border-gray-400 transition-colors"
                />
              ) : isPdf ? (
                <div className="w-16 h-16 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                  <FileText size={26} />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                  <ImageIcon size={26} />
                </div>
              )}
            </a>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{value.name}</p>
              <p className="text-xs text-gray-500">{formatBytes(value.size)}</p>
            </div>
            <div className="flex items-center gap-1">
              <a
                href={value.url}
                target="_blank"
                rel="noreferrer"
                className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-white transition-colors"
                title={t('documents.preview') as string}
                onClick={(e) => e.stopPropagation()}
              >
                {isImage ? <Eye size={16} /> : <ExternalLink size={16} />}
              </a>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-white transition-colors"
                title={t('common.replace') as string}
              >
                <RefreshCw size={16} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRequestRemove) {
                    onRequestRemove();
                  } else {
                    setConfirmDeleteOpen(true);
                  }
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-white transition-colors"
                title={t('common.remove') as string}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {errorMessage ? (
        <span className="text-xs font-medium text-red-600">{errorMessage}</span>
      ) : hint ? (
        <span className="text-xs text-gray-500">{hint}</span>
      ) : null}

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          onChange(null);
          setConfirmDeleteOpen(false);
        }}
        title={t('common.delete.title') as string}
        message={t('common.delete.message') as string}
        description={value?.name}
        confirmText={t('common.delete.confirm') as string}
        cancelText={t('common.cancel') as string}
        variant="danger"
      />
    </div>
  );
};

export default FileUpload;
